import React from "react";
import { Check, CheckCheck, AlertCircle } from "lucide-react";
import { MessageWithStatus } from "../../types";
import { Timestamp } from "firebase/firestore";
import ProfilePicture from "../ProfilePicture";
import { InlineSkeleton } from "../common/LoadingSkeleton";
import { sanitizeUserMessage } from "../../utils/sanitization";

interface MessageBubbleProps {
  message: MessageWithStatus;
  isMyMessage: boolean;
  showAvatar?: boolean;
  showDate?: boolean;
  onRetry?: (messageId: string) => void;
  otherUser?: {
    uid?: string;
    photoURL?: string | null;
    displayName?: string;
  };
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isMyMessage,
  showAvatar = false,
  showDate = false,
  onRetry,
  otherUser,
}) => {
  // Format time as HH:mm only
  const formatTime = (timestamp: any): string => {
    try {
      if (!timestamp) return "";
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    } catch {
      return "";
    }
  };

  const getStatusIcon = () => {
    if (!isMyMessage) return null;

    switch (message.status) {
      case "sending":
        return <InlineSkeleton size="sm" className="w-3 h-3" />;
      case "sent":
        return <Check className="w-3 h-3 text-white/60" />;
      case "failed":
        return (
          <button onClick={() => onRetry?.(message.id || "")} title="Retry">
            <AlertCircle className="w-3 h-3 text-white/80" />
          </button>
        );
      default:
        const isRead = message.readBy && message.readBy.length > 1;
        return isRead ? (
          <CheckCheck className="w-3 h-3 text-white/90" />
        ) : (
          <Check className="w-3 h-3 text-white/60" />
        );
    }
  };

  return (
    <div
      className={`flex ${isMyMessage ? "justify-end" : "justify-start"} mb-1`}
    >
      <div
        className={`flex items-end gap-2 max-w-[75%] ${
          isMyMessage ? "flex-row-reverse" : "flex-row"
        }`}
      >
        {/* Avatar */}
        {!isMyMessage && showAvatar && otherUser && (
          <ProfilePicture
            src={otherUser.photoURL}
            alt={otherUser.displayName || "User"}
            size="sm"
            className="w-7 h-7 flex-shrink-0 rounded-full"
            user={otherUser}
            userId={otherUser.uid}
          />
        )}

        {/* Bubble */}
        <div
          className={`
          relative px-3 py-2 shadow-sm
          ${
            isMyMessage
              ? "bg-[#da2516] text-white rounded-2xl rounded-br-sm"
              : "bg-white text-gray-900 rounded-2xl rounded-bl-sm"
          }
        `}
        >
          {/* Text */}
          <p
            className="text-[14px] leading-relaxed whitespace-pre-wrap break-words"
            dangerouslySetInnerHTML={{
              __html: sanitizeUserMessage(message.text || ""),
            }}
          />
          {/* Time + Status */}
          <div
            className={`flex items-center justify-end gap-1 mt-1 ${
              isMyMessage ? "text-white/70" : "text-gray-400"
            }`}
          >
            <span className="text-[10px]">{formatTime(message.timestamp)}</span>
            {isMyMessage && (
              <span className="flex items-center">{getStatusIcon()}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
